/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import {
    ApiService,
    LanguageService,
    LogService,
    NavigationService,
    NotifyService,
    RbacService,
    UtilsService
} from '../../../../../../core/services';
import { BaseComponent } from '../../../../../../core/components';
import {
    Component,
    ComponentRef,
    Input,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewContainerRef,
} from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import {
    FIPApiService,
    FipModel,
    HangingRealServersModalComponent,
    L7BackendServiceModel,
    L7RealServiceModel,
    L7VirtualServiceModel,
    Layer47WizardService,
    Layer4ApiService,
    Layer4Template,
    Layer7ApiService,
    Layer7Template,
    LockModel,
    LockService,
    MetricsEnum,
    ServicesUtilsService,
    StatusEnum,
    TemplateModel
} from '../../../../shared';
import { WidgetService } from '../../../../shared/services/utilities/widget.service';
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';

@Component({
    selector: 'app-service-details',
    templateUrl: './serviceDetails.component.html',
    styleUrls: ['./serviceDetails.component.scss']
})

export class ServiceDetailsComponent extends BaseComponent implements OnInit {
    /**
     * Reference to new service container.
     */
    @ViewChild('modalContainer', { read: ViewContainerRef, static: true })
    public modalContainer: any;

    /**
     * Get all the search input fields on the page for filtering.
     */
    @ViewChildren('search')
    public backendSearches: QueryList<any> = new QueryList<any>();

    /**
     * Define number of columns each widget should 'span' across.
     */
    @Input() public colSpan: number;

    /**
     * To store our frontends.
     */
    public frontend: L7VirtualServiceModel;

    /**
     * Our floating ip model.
     */
    public frontendFip: any;

    /**
     * Use to display backends in the view.
     */
    public backendRows: any[] = [];

    /**
     * Use to display backends in the view.
     */
    public realServerRows: any[] = [];

    /**
     * To store the list of hanging realservers.
     */
    public hangingReals: any[] = [];

    /**
     * Modal Component Reference.
     */
    public modalComponentReference: ComponentRef<any>;

    /**
     * Reference to function passed into the extended alert's widget.
     */
    public callBackReference: (...args: any[]) => void;

    /**
     * Set by socketData and used to store the statues instead of calling functions.
     */
    public serviceStatusByName: any = {};

    /**
     * A boolean to determine if the loading indicator should show or not.
     */
    public loading = true;

    /**
     * Stored transaction state
     */
    public transactionActive = false;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store the template type.
     */
    public templateType: string;

    /**
     * Store the parent filer to update the table rows.
     */
    public parentFilter: string = '';

    /**
     * Store the service id.
     * @private
     */
    private serviceId: string;

    /**
     * Store the api service set by service type.
     * @private
     */
    private selectedApiService: any;

    /**
     * Store all services in lookup.
     */
    public wsLookup: any = {};

    /**
     * List of backends.
     * @private
     */
    private backends: L7BackendServiceModel[] = [];

    /**
     * List of associated real servers.
     * @private
     */
    private realServers: L7RealServiceModel[] = [];

    /**
     * List of associated real servers.
     * @private
     */
    private acls: any[] = [];

    /**
     * Template that get emitted to the acl form with values when editing an acl.
     * @private
     */
    private templateSubject: Subject<object> = new Subject<object>();

    /**
     * Our floating ips.
     * @private
     */
    private fips: FipModel[];

    /**
     * Store the real servers for a given backend.
     * @private
     */
    private realsForBackend: any[] = [];

    /**
     * Store the backend ID of the backend that has been toggled open.
     * @private
     */
    private backendOpenedStore: any = [];

    /**
     * Used to flag if a server has been added or removed.
     * @private
     */
    private processingFlag = false;

    /**
     * Store haproxy data from websockets.
     * @private
     */
    private storedHaProxyData: any = {};

    constructor(
        public servicesUtilsService: ServicesUtilsService,
        public utilsService: UtilsService,
        private activatedRoute: ActivatedRoute,
        private apiService: ApiService,
        private fipsApiService: FIPApiService,
        private languageService: LanguageService,
        private layer47WizardService: Layer47WizardService,
        private layer4ApiService: Layer4ApiService,
        private layer7ApiService: Layer7ApiService,
        private lockService: LockService,
        private logService: LogService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private router: Router,
        private widgetService: WidgetService
    ) {
        super();

        this.setUpComponent();
        this.setupSubscribers();
        this.setupWidgetServiceSubscriber();
        this.registerButtons();
    }

    /**
     * getStatusEnums
     */
    get statusEnum(): any {
        return StatusEnum;
    }

    /**
     * Returns our template subject as an observable.
     */
    get template(): any {
        return this.templateSubject.asObservable();
    }

    public ngOnInit(): void {
        // Set up callback for search from extended alert widget.
        this.callBackReference = (name: string, filter: string) => this.filterTables(name, filter);

        this.transactionActive = this.lockService.getLockStateByProperty('transactionLocked');
    }

    /**
     * Update table rows with searched rows.
     * @param $event
     */
    public updateTableRows($event: any): void {
        this.backendRows = [...$event];
    }

    /**
     * Open the hanging backends modal.
     */
    public openHangingRealsModal(): void {
        const component: any = this.modalContainer.createComponent(HangingRealServersModalComponent);
        // Set the template in the wizard service.
        component.instance.hangingRealServerIds = this.hangingReals;
        component.instance.updateCallback = (): void => {
            this.setUpPageData();
        };

        // Assign component reference.
        this.modalComponentReference = component;
    }

    /**
     * Add backend button disabled.
     */
    public addBackendButtonDisabled(): boolean {
        return !this.permissionsLookup['service-details-add-backend'] ||
            this.transactionActive ||
            (this.templateType === 'layer4' && this.backendRows.length > 0);
    }

    /**
     * Get connections for service.
     * @param backendId
     * @param realId
     * @private
     */
    private getConnectionsForService(backendId: string, realId?: string): any {
        if (!this.wsLookup?.hasOwnProperty('backends')) {
            return 'pending';
        }

        let service: any;
        if (this.wsLookup['backends'].hasOwnProperty(backendId)) {
            const backend = this.wsLookup['backends'][backendId];
            if (realId) {
                if (backend.hasOwnProperty('reals') && backend.reals.hasOwnProperty(realId)) {
                    service = this.wsLookup['backends'][backendId].reals[realId];
                } else {
                    return 'N/A';
                }
            } else {
                service = backend;
            }

            return service.connection.connections.toString();
        } else {
            return 'N/A';
        }
    }

    /**
     * Get the status of a service.
     * @param backendId
     * @param realId
     * @private
     */
    private getStatusForService(backendId: string, realId?: string): any {
        if (!this.wsLookup?.hasOwnProperty('backends')) {
            return 'pending';
        }

        const service: any = realId ? this.wsLookup['backends'][backendId].reals[realId] : this.wsLookup['backends'][backendId];
        const status: string = service.status;

        if (!status) {
            return 'pending';
        } else {
            return typeof status === 'string' ? this.utilsService.getStatusDisplayName(status) : status;
        }
    }

    /**
     * Start of new real server the wizard process.
     * @param backend
     * @param duplicate
     */
    public addEditBackend(backend?: any, duplicate = false): void {
        const redirectUrl: string = backend ? '/service/edit/' + backend.id : '/service/add';

        const navigationExtras: NavigationExtras = {
            state: {
                cancelText: 'services_return_to_service_details',
                duplicate: duplicate,
                editMode: !!backend,
                parentUrl: `/cluster-configuration/manage-services/service-details/${this.templateType}/${this.frontend.id}`,
                serviceType: 'backend',
                templateType: this.templateType,
                templates: this.getBackendTemplate(backend, duplicate),
                wizardTitle: this.languageService.translate(this.getBackendServiceTitle(backend, duplicate))
            }
        };

        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(redirectUrl), navigationExtras).then().catch();
    }

    /**
     * Get template.
     * @param backend
     * @param duplicate
     * @private
     */
    private getBackendTemplate(backend: any, duplicate?: boolean): TemplateModel {
        const availableTemplates: TemplateModel[] = this.getFormattedTemplates();
        const services: any = { frontend: this.frontend, backends: this.backends, fips: this.fips };
        this.layer47WizardService.setServices(services);
        const backendId: string = backend?.id ? backend.id : null;

        return this.layer47WizardService.setUpBackendTemplate(backendId, availableTemplates, this.templateType, duplicate);
    }

    /**
     * Get formatted templates.
     * @private
     */
    private getFormattedTemplates(): TemplateModel[] {
        return this.templateType === 'layer4' ? [Layer4Template] : [Layer7Template];
    }

    /**
     * Get the backend title.
     * @param backend
     * @param duplicate
     * @private
     */
    private getBackendServiceTitle(backend?: any, duplicate?: boolean): string {
        if (backend) {
            return 'services_edit_backend_service';
        } else if (duplicate) {
            return 'services_duplicate_backend_service';
        } else {
            return 'services_add_backend_service';
        }
    }

    /**
     * Toggle to open or close the backend server details.
     * @param index
     */
    public toggleCollapse(index: number): void {
        if (this.backendRows[index].collapsed) {
            this.backendOpenedStore.push(this.backendRows[index].id);
        } else {
            const idIndex = this.backendOpenedStore.indexOf(this.backendRows[index].id);
            this.backendOpenedStore.splice(idIndex, 1);
        }
        this.backendRows[index].collapsed = !this.backendRows[index].collapsed;
    }

    /**
     * Route to acl details page.
     */
    public routerToAclDetails(): void {
        this.router.navigate(
            ['../../../../manage-acls/acls-details/', this.templateType, this.frontend.id],
            { relativeTo: this.activatedRoute }
        )
            .then().catch();
    }

    /**
     * Set up the real servers modal for either add or edit.
     * @param backendId
     * @param realServerId
     * @param duplicate
     */
    public addEditRealServer(backendId: string, realServerId?: string, duplicate = false): void {
        const url: string = realServerId ? '/service/edit/' + realServerId : '/service/add';

        const navigationExtras: NavigationExtras = {
            state: {
                cancelText: 'services_return_to_service_details',
                duplicate: duplicate,
                editMode: realServerId,
                parentUrl: `/cluster-configuration/manage-services/service-details/${this.templateType}/${this.frontend.id}`,
                serviceType: 'realServer',
                templateType: this.templateType,
                templates: this.getRealServerTemplate(backendId, realServerId, duplicate),
                wizardTitle: this.languageService.translate(this.getRealServerServiceTitle(realServerId, duplicate)),
                options: { showBackButton: false, showSaveButton: !realServerId }
            }
        };

        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(url), navigationExtras).then().catch();
    }

    /**
     * Get real server template.
     * @param backendId
     * @param realServerId
     * @param duplicate
     * @private
     */
    private getRealServerTemplate(backendId: string, realServerId?: string, duplicate = false): TemplateModel {
        const availableTemplates: TemplateModel[] = this.getFormattedTemplates();
        const services: any = { realServers: this.realServers, backends: this.backends, fips: this.fips };
        this.layer47WizardService.setServices(services);

        return this.layer47WizardService.setUpRealServerTemplate(realServerId, backendId, availableTemplates, duplicate);
    }

    /**
     * Get the title for Real server wizard.
     * @param realServerId
     * @param duplicate
     * @private
     */
    private getRealServerServiceTitle(realServerId: string, duplicate: boolean): string {
        if (duplicate && realServerId) {
            return 'services_duplicate_real_server';
        } else if (realServerId) {
            return 'services_edit_real_server';
        } else {
            return 'services_add_real_server';
        }
    }

    /**
     * Perform an action on a real server.
     * @param action
     * @param row
     */
    public realServerAction(action: string, row: any): void {
        const options: any = {
            onCompleteCallback: this.serviceActionHelper.bind(this, action, row),
            logMessageTitle: 'ServiceDetails: RealServerAction',
            templateType: this.templateType
        };

        this.servicesUtilsService.actionService(action, row, options);
    }

    /**
     * Backend Action
     * @param action
     * @param backend
     */
    public backendAction(action: string, backend: any): void {
        const options: any = {
            onCompleteCallback: this.serviceActionHelper.bind(this, action, backend),
            logMessageTitle: 'ServiceDetails: backendAction()',
            templateType: this.templateType
        };

        this.servicesUtilsService.actionService(action, backend, options);
    }

    /**
     * Set up component.
     * @private
     */
    private setUpComponent(): void {
        this.serviceId = this.activatedRoute.snapshot.params['id'];
        const serviceType: any = this.activatedRoute.snapshot.params['serviceType'];
        this.templateType = serviceType;
        this.selectedApiService = this.setApiBasedOnServiceType(serviceType);
        if (this.selectedApiService) {
            this.setUpPageData();
        } else {
            this.setError();
        }
    }

    /**
     * Set the API service for this page based on the service type.
     * @param type
     * @private
     */
    private setApiBasedOnServiceType(type: string): any {
        switch (type) {
            case 'layer4':
                return this.layer4ApiService;
            case 'layer7':
            case 'monitor':
                return this.layer7ApiService;
            default:
                this.logService.error(
                    'ServiceDetailsComponent:setApiBasedOnServiceType',
                    'No matching api service found'
                );

                return null;
        }
    }

    /**
     * Set up the data for the page.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls: any = {
            frontend: this.selectedApiService.getFrontend(this.serviceId).pipe(this.apiService.handleRequest([]).bind(this)),
            backends: this.selectedApiService.getBackendsByFrontendId(this.serviceId).pipe(this.apiService.handleRequest([]).bind(this)),
            hangingReals: this.selectedApiService.getHangingRealServers().pipe(this.apiService.handleRequest([]).bind(this)),
            fips: this.fipsApiService.getFloatingIPs().pipe(this.apiService.handleRequest([]).bind(this)),
            acls: this.selectedApiService.getAclRules().pipe(this.apiService.handleRequest([]).bind(this))
        };

        forkJoin(apiCalls)
            .pipe(
                map((result) => result),
                takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    if (dataGroup['frontend']) {
                        this.frontend = dataGroup['frontend'];
                    }

                    if (dataGroup['backends']?.length) {
                        this.backends = dataGroup['backends'];
                        this.setUpRealServers();
                    } else {
                        this.loading = false;
                    }

                    if (dataGroup['hangingReals']) {
                        this.hangingReals = dataGroup['hangingReals'];
                    }

                    if (dataGroup['fips']) {
                        this.fips = dataGroup['fips'];
                        this.frontendFip = this.fips.filter((fip: FipModel): boolean => fip.id === this.frontend.fipId)[0] || { ip: 'n/a' };
                    }
                    if (dataGroup['acls']) {
                        this.acls = dataGroup['acls'];
                    }
                }
                ,
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ServiceDetailsComponent: setUpPageData()', error.message);
                    this.setError();
                }
            });
    }

    /**
     * Subscribe to the lock service emitter.
     * @private
     */
    private setupSubscribers(): void {
        this.lockService.locksEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: LockModel): void => {
                    if (data.hasOwnProperty('transactionLocked')) {
                        this.transactionActive = data.transactionLocked;
                    }
                },
                error: (error: Error): void => {
                    this.logService.error(
                        'ServiceDetailsComponent:setupSubscribers',
                        'Unable to subscribe to the lock service',
                        error
                    );
                }
            });
    }

    /**
     * Callback helper function to execute functions depending on service type and action.
     * @param action
     * @param row
     * @private
     */
    private serviceActionHelper(action: string, row?: { id: any; }): void {
        const { title, serviceType } = this.servicesUtilsService.getActionTypeAndTitle(action);

        if (title === 'delete') {
            if (serviceType === 'Backend') {
                this.removeBackendFromPage(row.id);
            }

            if (serviceType === 'RealServer') {
                this.setUpRealServers();
            }

            this.getUpdatedBackendsData();
        }
    }

    /**
     * Find backend in backendRows and delete it.
     * @param id
     * @private
     */
    private removeBackendFromPage(id: any): void {
        const index: number = this.backendRows.findIndex(backend => backend.id === id);
        this.backendRows.splice(index, 1);
    }

    /**
     * Set up the real servers for the backends.
     * @private
     */
    private setUpRealServers(): void {
        let index = 1;
        let realsForBackend: any[] = [];
        const backendsToSubmit: Observable<L7BackendServiceModel> = from(this.backends);
        const call: Observable<any> = backendsToSubmit.pipe(
            mergeMap(
                (row: L7BackendServiceModel) => this.selectedApiService.getRealServersByBackendId(row.id)
                    .pipe(catchError(error => of(error)))
            )
        );
        call.subscribe({
            next: (response: any): void => {
                realsForBackend = realsForBackend.concat(response);
                if (index === this.backends.length) {
                    this.realsForBackend = realsForBackend;
                    this.populateRows();
                    this.processingFlag = true;
                    this.loading = false;
                }
                index++;
            }
        });
    }

    /**
     * Once a backend has been deleted, get the latest details from HA Proxy.
     * @private
     */
    private getUpdatedBackendsData(): void {
        this.loading = true;
        this.selectedApiService.getBackendsByFrontendId(this.frontend.id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: L7BackendServiceModel[]): void => {
                    this.backends = response;
                    if (!this.backends.length) {
                        this.loading = false;
                    }
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ServiceDetailsComponent: getUpdatedBackendsData', error.message);
                }
            });
    }

    /**
     * Filter tables.
     * @param name
     * @param filter
     * @private
     */
    private filterTables(name: string, filter: string): void {
        this.filterParent(name, filter);
    }

    /**
     * Filter parent.
     * @param name
     * @param filter
     * @private
     */
    private filterParent(name: string, filter: string): void {
        this.parentFilter = '';
        // We use the setTimeouts here to give the search component time to update.
        const parentSearchTimeout = setTimeout(() => {
            const parentSearch = this.backendSearches.toArray();
            parentSearch[0].returnSingleton = true;
            this.parentFilter = name;
            const searchTimeout = setTimeout(() => {
                this.filterChildren(name, filter);
                clearTimeout(searchTimeout);
            }, 250);
            clearTimeout(parentSearchTimeout);
        }, 250);
    }

    /**
     * Filter table children.
     * @param name
     * @param filter
     * @private
     */
    private filterChildren(name: string, filter: string): void {
        const erroredBackendIndex: number = this.backendRows.findIndex(row => row.name === name);
        this.backendRows[erroredBackendIndex].search = filter;
        this.backendRows[erroredBackendIndex].collapsed = false;
    }

    /**
     * Populate rows of backends and add associated real servers.
     * @private
     */
    private populateRows(): void {
        // Reset our rows variable.
        this.backendRows = [];
        this.realServers = [];
        this.realServerRows = [];
        this.serviceStatusByName = {};

        for (const backend of this.backends) {
            let rips: any[] = [];

            // Get row content.
            if (backend.ripIds?.length) {
                const ripsByBackend = this.getRipsFromBackend(this.realsForBackend, backend.ripIds);
                rips = this.formatRipsData(ripsByBackend, backend);
            }
            this.realServerRows.push(rips);
            const getCollapsedValue: number = this.backendOpenedStore.indexOf(backend.id);
            const acl: any = this.acls.find(item => item.redirectLocation === backend.id);

            this.backendRows.push({
                id: backend.id,
                name: backend.name,
                connections: this.getConnectionsForService(backend.id),
                status: this.formatServiceStatus(this.getStatusForService(backend.id)),
                rips,
                search: '',
                collapsed: getCollapsedValue === -1,
                acl,
                realServers: rips
            });
        }
        // Check if we only have one backend, if so have it set.
        if (this.backendRows.length === 1) {
            this.backendRows[0].collapsed = false;
        }
    }

    /**
     * Map a set of real servers based on backend rip ids.
     * @param realServers
     * @param ripsIds
     * @private
     */
    private getRipsFromBackend(realServers: any[], ripsIds: string | any[]): any[] {
        const associatedRips: any[] = [];
        for (const server of realServers) {
            if (ripsIds.includes(server.id)) {
                associatedRips.push(server);
            }
        }

        return associatedRips;
    }

    /**
     * Format the rips data and include socket data for live feeds.
     * @param ripsByBackend
     * @param backend
     * @private
     */
    private formatRipsData(ripsByBackend: any, backend: L7BackendServiceModel): any {
        const formattedRips: any[] = [];

        let index = 0;
        for (const rip of ripsByBackend) {
            // Add each real server to the class level variable if it doesn't already exist.
            if (!this.realServers.includes(rip.id)) {
                rip.backendId = backend.id;
                this.realServers.push(rip);
            }

            formattedRips.push({
                index,
                backendLabel: backend.name,
                connections: this.getConnectionsForService(backend.id, rip.id),
                id: rip.id,
                ip: rip.ip,
                name: rip.name,
                port: rip.port,
                usage: rip.usage,
                status: this.formatServiceStatus(this.getStatusForService(backend.id, rip.id))
            });
            index++;
        }

        return formattedRips;
    }

    /**
     * Check data for changes.
     * @param data
     * @private
     */
    private checkDataForChanges(data: any): void {
        if ((JSON.stringify(data) !== JSON.stringify(this.storedHaProxyData)) || this.processingFlag) {
            this.storedHaProxyData = data;
            this.processingFlag = false;
            this.processWebSocketData();
        }
    }

    /**
     * Subscribe to requested socket data (services) and assigned to class level variable.
     * Once all the data is collected, populate the table rows.
     * @private
     */
    private setupWidgetServiceSubscriber(): void {
        this.widgetService.haDataEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    this.checkDataForChanges(response);
                }
            });
    }

    /**
     * Process web socket data.
     * @private
     */
    private processWebSocketData(): void {
        this.storeWsDataByService();

        if (this.backendRows.length) {
            this.updateServiceStatuses();
        }

        if (this.realServerRows.length) {
            this.updateServiceStatuses(true);
        }
    }

    /**
     * Store websocket data in lookup.
     * @private
     */
    private storeWsDataByService(): void {
        const data = this.storedHaProxyData;

        if (!data) {
            this.logService.warn('ServiceDetailsComponent:storeWsDataByService', 'Invalid websocket data received');

            return;
        }

        if (data.frontends?.length) {
            this.wsLookup['frontend'] = data.frontends.find((item: any): boolean => item.id === this.serviceId);
        }

        if (data.backends?.length) {
            if (this.backends?.length) {
                this.wsLookup['backends'] = {};
                for (const backend of this.backends) {
                    const wsBackend: any = data.backends.find((item: any): boolean => item.id === backend.id);
                    const backendClone = wsBackend;

                    if (!backendClone) {
                        continue;
                    }

                    this.wsLookup['backends'][backendClone.id] = backendClone;

                    if (this.realsForBackend?.length) {
                        const wsRealsForBackend: any[] = data.reals?.filter((item: any): boolean => item.backendId === backend.id) || [];
                        if (wsRealsForBackend?.length) {
                            this.wsLookup['backends'][backendClone.id].reals = {};
                            for (const real of wsRealsForBackend) {
                                this.wsLookup['backends'][backendClone.id].reals[real.realId] = real;
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * Update service statuses.
     * @param updateReals
     * @private
     */
    private updateServiceStatuses(updateReals?: boolean): void {
        this.backendRows.forEach((backend: any, index: number): void => {
            backend.status = this.formatServiceStatus(this.getStatusForService(backend.id));
            backend.connections = this.getConnectionsForService(backend.id);

            if (updateReals) {
                for (const real of this.backendRows[index]['realServers']) {
                    real.status = this.formatServiceStatus(this.getStatusForService(backend.id, real.id));
                    real.connections = this.getConnectionsForService(backend.id, real.id);
                }
            }
        });
    }

    /**
     * Format the service status.
     * @param status
     * @private
     */
    private formatServiceStatus(status: string): string {
        return this.utilsService.firstLetterUppercase(status);
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference: any = {
            'service-details-add-backend': this.getAddBackendPermissions(),
            'service-details-edit-backend': this.getEditBackendPermissions(),
            'service-details-delete-backend': [{ path: '/haproxy/backends/:id', methods: ['DELETE'] }],
            'service-details-online-backend': [{ path: '/haproxy/backends/:id/online', methods: ['PUT'] }],
            'service-details-drain-backend': [{ path: '/haproxy/backends/:id/drain', methods: ['PUT'] }],
            'service-details-halt-backend': [{ path: '/haproxy/backends/:id/halt', methods: ['PUT'] }],
            'service-details-hanging-reals': [
                { path: '/ipvsDirector/rips', methods: ['GET'] },
                { path: '/haproxy/rips', methods: ['GET'] }
            ],
            'service-details-add-real-server': this.getAddRealServerPermissions(),
            'service-details-edit-real-server': this.getEditRealServerPermissions(),
            'service-details-delete-real-server': [{ path: '/haproxy/rips/:id', methods: ['DELETE'] }],
            'service-details-online-real-server': [{ path: '/haproxy/rips/:id/online', methods: ['PUT'] }],
            'service-details-drain-real-server': [{ path: '/haproxy/rips/:id/drain', methods: ['PUT'] }],
            'service-details-halt-real-server': [{ path: '/haproxy/rips/:id/halt', methods: ['PUT'] }],
            'service-details-restart-haproxy': [{ path: '/services/:service/restart', methods: ['PUT'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * Get service details edit backend
     * @private
     */
    private getAddBackendPermissions(): any[] {
        return [
            { path: '/haproxy/frontends', methods: ['GET'] },
            { path: '/haproxy/backends', methods: ['GET', 'POST'] },
            { path: '/haproxy/acls', methods: ['GET', 'POST'] },
            { path: '/fips', methods: ['GET'] },
            { path: '/haproxy/frontends/:id', methods: ['GET', 'PUT'] },
            { path: '/haproxy/frontends/:id/backends', methods: ['GET'] },
            { path: '/healthchecks/service/:service', methods: ['GET'] },
            { path: '/fallback', methods: ['GET'] },
            { path: '/locks', methods: ['GET'] }
        ];
    }

    /**
     * Get service details edit backend
     * @private
     */
    private getEditBackendPermissions(): any[] {
        return [
            { path: '/haproxy/frontends', methods: ['GET'] },
            { path: '/haproxy/backends', methods: ['GET', 'POST'] },
            { path: '/haproxy/acls', methods: ['GET', 'POST'] },
            { path: '/fips', methods: ['GET'] },
            { path: '/haproxy/frontends/:id', methods: ['GET', 'PUT'] },
            { path: '/haproxy/frontends/:id/backends', methods: ['GET'] },
            { path: '/healthchecks/service/:service', methods: ['GET'] },
            { path: '/fallback', methods: ['GET'] },
            { path: '/locks', methods: ['GET'] }
        ];
    }

    /**
     * Get service details edit backend
     * @private
     */
    private getAddRealServerPermissions(): any[] {
        return [
            { path: '/haproxy/frontends', methods: ['GET'] },
            { path: '/haproxy/backends', methods: ['GET'] },
            { path: '/haproxy/acls', methods: ['GET'] },
            { path: '/fips', methods: ['GET'] },
            { path: '/haproxy/frontends/:id', methods: ['GET'] },
            { path: '/haproxy/frontends/:id/backends', methods: ['GET'] },
            { path: '/haproxy/rips', methods: ['GET', 'POST'] },
            { path: '/locks', methods: ['GET'] }
        ];
    }

    /**
     * Get service details edit backend
     * @private
     */
    private getEditRealServerPermissions(): any[] {
        return [
            { path: '/haproxy/frontends', methods: ['GET'] },
            { path: '/haproxy/backends', methods: ['GET'] },
            { path: '/haproxy/acls', methods: ['GET'] },
            { path: '/fips', methods: ['GET'] },
            { path: '/haproxy/frontends/:id', methods: ['GET'] },
            { path: '/haproxy/frontends/:id/backends', methods: ['GET'] },
            { path: '/haproxy/rips', methods: ['GET'] },
            { path: '/haproxy/rips/:id', methods: ['PUT'] },
            { path: '/locks', methods: ['GET'] }
        ];
    }

    /**
     * Set error.
     * @private
     */
    private setError(): void {
        this.notifyService.error('service_details_error_type', { messageTranslate: true });
        this.loading = false;
    }

    protected readonly MetricsEnum = MetricsEnum;
}
